/** Identifies one of the four masks a user can choose to display dates in. */
export type DateFormatId = 'MDY' | 'DMY' | 'ISO' | 'MED';

/** One row of the mask table: an id, the moment format pattern it renders through, and its hint text. */
export interface DateFormatMask {
  id: DateFormatId;
  pattern: string;
  hint: string;
}

/**
 * The four date masks, in display order.
 * @see docs/projects/request-submission/DATE_FORMAT_PREFERENCE_SPEC.md — §The masks
 */
export const DATE_FORMAT_MASKS: DateFormatMask[] = [
  { id: 'MDY', pattern: 'MM/DD/YYYY', hint: 'US' },
  { id: 'DMY', pattern: 'DD/MM/YYYY', hint: 'UK & EU' },
  { id: 'ISO', pattern: 'YYYY-MM-DD', hint: 'ISO 8601' },
  { id: 'MED', pattern: 'DD MMM YYYY', hint: 'Unambiguous' }
];

/** The same four masks keyed by id, so a lookup by a `DateFormatId` is total and needs no fallback. */
const MASKS_BY_ID = Object.fromEntries(DATE_FORMAT_MASKS.map((mask) => [mask.id, mask])) as Record<
  DateFormatId,
  DateFormatMask
>;

/**
 * The mask a `DateFormatId` names. Total by construction — every id in the union is a row of the table.
 * @param {DateFormatId} id - The chosen mask id.
 * @returns {DateFormatMask} That row of the mask table.
 */
export const maskFor = (id: DateFormatId): DateFormatMask => MASKS_BY_ID[id];

/** The mask applied when nothing valid is stored. Fixed — no locale detection of any kind. */
export const DEFAULT_DATE_FORMAT: DateFormatId = 'MDY';

/** localStorage key for the persisted preference. Unscoped by user or version — per browser only. */
export const DATE_FORMAT_STORAGE_KEY = 'npi.vantage.dateFormat';

/**
 * Narrows an unknown value to a `DateFormatId` by membership in the mask table.
 * @param {unknown} value - A candidate read from storage, already `JSON.parse`d.
 * @returns {boolean} Whether `value` is one of the four recognised mask ids.
 */
export const isDateFormatId = (value: unknown): value is DateFormatId =>
  DATE_FORMAT_MASKS.some((mask) => mask.id === value);
